import moment from 'moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Resource, ResourceConstructorArgs } from './Resource'
import { FKType } from './SQLBuilder'
import { User } from './User'

export type RequirementConstructorArgs = Omit<ConstructorArgs<() => Requirement>, 'resources'> & {
    resources: string | Resource[] | ResourceConstructorArgs[] | null
}

export class Requirement extends BaseRecord {
    readonly recordType = 'requirement'

    name?: string
    color?: string
    seqnum?: number
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    resources?: Resource[]

    constructor(data?: Partial<RequirementConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator?.owner
            return
        }

        this.name = data.name ?? undefined
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.color = data.color ?? undefined
        this.resources = (() => {
            if (data.resources === undefined || data.resources === null) {
                return []
            }

            if (typeof data.resources === 'string') {
                const resource = this.getForeignRecord('resources', Resource)
                return resource !== undefined ? [resource] : []
            }

            return data.resources.map((resource) => {
                if (resource instanceof Resource) {
                    return resource
                }
                return new Resource(resource)
            })
        })()
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<RequirementConstructorArgs>): Requirement {
        return new Requirement({ ...this.serializeForClone(), ...overrides })
    }

    // static getColors(): string[] {
    //     return [
    //         kColorCobalt,
    //         kColorMango,
    //         kColorTomato,
    //         kColorGrape,
    //         kColorAvocado,
    //         kColorCocoa,
    //         kColorBlueberry,
    //         kColorPumpkin,
    //         kColorRadicchio,
    //         kColorAmethyst,
    //         kColorBanana,
    //         kColorGraphite,
    //         kColorPeacock,
    //         kColorCitron,
    //         kColorCherryBlossom,
    //         kColorWisteria,
    //         kColorTangerine,
    //         kColorLavender,
    //         kColorFlamingo,
    //         kColorBirch
    //     ]
    // }

    resourceStrings(): string[] {
        if (!this.resources) return []
        return this.resources.map((resource) => resource.name || '')
    }

    deserialize(data: any): void {
        super.deserialize(data)
        this.creator = BaseRecord.fromSerializer(data.creator, User)
        this.owner = BaseRecord.fromSerializer(data.owner, User)
        this.resources = data.resources.map((resource: any) =>
            BaseRecord.fromSerializer(resource, Resource)
        )
        this.lastModifiedDate = moment(data.last_modified_date)
        this.cancelledDate = moment(data.cancelled_date)
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict.name = this.name
        dict.color = this.color
        if (this.resources) {
            const resources = this.resources.map((resource) => resource.id)
            dict.resources = resources
        }
        return dict
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        resources: ['resources'],
    }

    static dataType(): DataType {
        return 'requirements'
    }

    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
}
